import { EV } from '../core/eventbus.js';
import { Repo } from '../data/schema.js';
import { CERT_DEFS } from '../data/tags.js';
import { getItem } from '../data/items.js';
import { Shops } from '../systems/shops.js';
import { Jobs, JOB_DEFS, QUEST_DEFS, UNIT_DEFS, LICENSE_STEPS } from '../systems/jobs.js';
import { Avatar } from '../systems/avatar.js';
import { Tags } from '../systems/tags.js';

/**
 * 城里的几块面板：买卖、告示榜、衙署（办照）、摊位地皮、短工市。
 *
 * 这一层只管「把账算给人看、把按钮点出去」：钱货互易、行的工钱、证照流程
 * 都在 systems 里；这里一行 UI 也不自己判能不能，拦下来只负责把话说清楚。
 *
 * 面板靠 EV.SHOP_OPEN / BOARD_OPEN / LICENSE_OPEN / STALL_OPEN / HIRE_OPEN 打开，
 * 发出的地方在 systems/actions.js（走到柜台、告示榜、衙署窗口前按 E）。
 */
const NAMES = ['shop', 'board', 'license', 'stall', 'hire'];

function cashOf(db) {
  return Math.round(Repo.getPlayer(db).cash || 0);
}

function dayOf(db) {
  return Repo.getWorld(db).day || 1;
}

export class CityPanels {
  constructor(engine, { toast } = {}) {
    this.engine = engine;
    this.db = engine.db;
    this.toast = toast || ((text, kind) => engine.events.emit(EV.TOAST, { text, kind }));
    this.shopId = '';
    this.tab = 'places';
    this.current = '';
    this.el = {
      shop: document.getElementById('shop'),
      shopTitle: document.getElementById('shop-title'),
      shopInfo: document.getElementById('shop-info'),
      shopBuy: document.getElementById('shop-buy-list'),
      shopSell: document.getElementById('shop-sell-list'),

      board: document.getElementById('board'),
      boardBody: document.getElementById('board-body'),
      tabPlaces: document.getElementById('board-tab-places'),
      tabJobs: document.getElementById('board-tab-jobs'),
      tabQuests: document.getElementById('board-tab-quests'),
      boardRefresh: document.getElementById('board-refresh'),

      license: document.getElementById('license'),
      licenseInfo: document.getElementById('license-info'),
      licenseBody: document.getElementById('license-body'),

      stall: document.getElementById('stall'),
      stallInfo: document.getElementById('stall-info'),
      stallBody: document.getElementById('stall-body'),

      hire: document.getElementById('hire'),
      hireInfo: document.getElementById('hire-info'),
      hirePool: document.getElementById('hire-pool'),
      hireMine: document.getElementById('hire-mine'),
    };
    // 没有对应 DOM（比如 Node 里跑冒烟测试）就安静退场，不打扰别人
    if (!this.el.shop) return;
    this.bind();
  }

  /* ------------------------------ 开关 ------------------------------ */

  bind() {
    for (const el of document.querySelectorAll('[data-city-close]')) {
      el.onclick = () => this.close();
    }
    if (this.el.tabPlaces) this.el.tabPlaces.onclick = () => this.setTab('places');
    if (this.el.tabJobs) this.el.tabJobs.onclick = () => this.setTab('jobs');
    if (this.el.tabQuests) this.el.tabQuests.onclick = () => this.setTab('quests');
    if (this.el.boardRefresh) this.el.boardRefresh.onclick = () => { this.reset(); this.render(); };

    this.onRow(this.el.shopBuy, (act, id) => {
      const n = act === 'buy5' ? 5 : 1;
      const res = Shops.buy(this.db, this.shopId, id, n);
      this.say(res);
    });
    this.onRow(this.el.shopSell, (act, id) => {
      const n = act === 'sellAll' ? Repo.countItem(this.db, id) : 1;
      const res = Shops.sell(this.db, this.shopId, id, n);
      this.say(res);
    });
    this.onRow(this.el.boardBody, (act, id, arg) => this.onBoard(act, id, arg));
    this.onRow(this.el.licenseBody, (act, id) => {
      if (act === 'apply') this.say(Jobs.applyLicense(this.db, id));
    });
    this.onRow(this.el.stallBody, (act, id) => {
      if (act === 'buy') this.say(Jobs.buyUnit(this.db, id));
      if (act === 'rent') this.say(Jobs.rentUnit(this.db, id));
    });
    this.onRow(this.el.hirePool, (act, id) => {
      if (act === 'hire') this.say(Jobs.hireStaff(this.db, id));
    });
    this.onRow(this.el.hireMine, (act, id, arg) => {
      if (act === 'assign') this.say(Jobs.assignStaff(this.db, id, arg));
    });

    this.engine.events.on(EV.SHOP_OPEN, ({ shopId }) => this.openShop(shopId));
    this.engine.events.on(EV.BOARD_OPEN, ({ tab }) => this.openBoard(tab === 'quests' ? 'quests' : 'places'));
    this.engine.events.on(EV.LICENSE_OPEN, () => this.openLicense());
    this.engine.events.on(EV.STALL_OPEN, ({ scene }) => this.openStall(scene));
    this.engine.events.on(EV.HIRE_OPEN, () => this.openHire());
  }

  /** 行内的按钮统一走这条路：找到 data-act 才响应 */
  onRow(container, fn) {
    if (!container) return;
    container.addEventListener('click', (e) => {
      const btn = e.target && e.target.closest ? e.target.closest('[data-act]') : null;
      if (!btn || !container.contains(btn)) return;
      fn(btn.dataset.act, btn.dataset.id, btn.dataset.arg || '');
      this.render();
    });
  }

  say(res) {
    if (!res) return;
    if (res.ok) this.toast(res.text, 'good');
    else this.toast(res.reason, 'warn');
  }

  toggle(name, force) {
    const el = this.el[name];
    if (!el) return;
    el.classList.toggle('hidden', force === false);
  }

  close() {
    for (const n of NAMES) if (this.el[n]) this.el[n].classList.add('hidden');
    this.current = '';
  }

  open(name) {
    if (!this.el[name]) return;
    this.close();
    this.el[name].classList.remove('hidden');
    this.current = name;
    this.render();
  }

  openShop(shopId) {
    this.shopId = shopId;
    this.open('shop');
  }

  openBoard(tab) {
    this.tab = tab || 'places';
    this.open('board');
  }

  openLicense() { this.open('license'); }

  openStall(scene) {
    this.scene = scene || '';
    this.open('stall');
  }

  openHire() { this.open('hire'); }

  /** 「再看看」：今天已经生成的活算了，明天才会出新一批 */
  reset() {
    const db = this.db;
    for (const r of db.table('jobOffers').all()) db.table('jobOffers').remove(r.id);
    for (const r of db.table('questOffers').all()) db.table('questOffers').remove(r.id);
  }

  setTab(tab) {
    this.tab = tab;
    this.render();
  }

  render() {
    if (!this.current) return;
    if (this.current === 'shop') this.renderShop();
    if (this.current === 'board') this.renderBoard();
    if (this.current === 'license') this.renderLicense();
    if (this.current === 'stall') this.renderStall();
    if (this.current === 'hire') this.renderHire();
  }

  /* ------------------------------ 行 ------------------------------ */

  row(target, { name, sub = '', acts = [], off = false }) {
    if (!target) return;
    const div = document.createElement('div');
    div.className = 'city-row' + (off ? ' off' : '');
    const buttons = acts.map((a) =>
      `<button data-act="${a.act}" data-id="${a.id || ''}" data-arg="${a.arg || ''}">${a.text}</button>`).join('');
    div.innerHTML = `<div class="grow"><b>${name}</b>${sub ? `<div class="sub">${sub}</div>` : ''}</div>${buttons}`;
    target.appendChild(div);
  }

  fill(target, text) {
    if (!target) return;
    target.innerHTML = '';
    if (text) {
      const div = document.createElement('div');
      div.className = 'city-empty';
      div.textContent = text;
      target.appendChild(div);
    }
  }

  /* ------------------------------ 买卖 ------------------------------ */

  renderShop() {
    const shop = Shops.SHOPS[this.shopId];
    if (!shop) { this.fill(this.el.shopBuy, '这里不做买卖。'); this.fill(this.el.shopSell, ''); return; }
    const open = Shops.isOpen(this.db, this.shopId);
    this.el.shopTitle.textContent = `${shop.name} · ${shop.owner}`;
    this.el.shopInfo.textContent =
      `${Shops.openHint(this.shopId)}　${open ? '这会儿开着门' : '这会儿没开门'}　`
      + `随身${cashOf(this.db)}钱　${Avatar.describe(this.db)}`;

    const buyBox = this.el.shopBuy;
    buyBox.innerHTML = '';
    for (const s of Shops.shelf(this.db, this.shopId)) {
      this.row(buyBox, {
        name: s.name,
        sub: `${s.price}钱一份　柜上${s.stock}件`,
        acts: open ? [{ act: 'buy1', id: s.id, text: '买' }, { act: 'buy5', id: s.id, text: '买5' }] : [],
        off: !open,
      });
    }
    if (!buyBox.children.length) this.fill(buyBox, '柜上空着。');

    const sellBox = this.el.shopSell;
    sellBox.innerHTML = '';
    for (const s of Shops.sellable(this.db, this.shopId)) {
      this.row(sellBox, {
        name: `${s.name}　×${s.have}`,
        sub: `收货${s.price}钱一件`,
        acts: open ? [{ act: 'sell1', id: s.id, text: '卖' }, { act: 'sellAll', id: s.id, text: '全卖' }] : [],
        off: !open,
      });
    }
    if (!sellBox.children.length) this.fill(sellBox, '这家不收你身上的东西。');
  }

  /* ------------------------------ 告示榜 ------------------------------ */

  renderBoard() {
    for (const [key, btn] of [['places', this.el.tabPlaces], ['jobs', this.el.tabJobs], ['quests', this.el.tabQuests]]) {
      if (btn) btn.classList.toggle('on', this.tab === key);
    }
    const body = this.el.boardBody;
    body.innerHTML = '';
    if (this.tab === 'places') this.renderPlaces(body);
    if (this.tab === 'jobs') this.renderJobs(body);
    if (this.tab === 'quests') this.renderQuests(body);
  }

  renderPlaces(body) {
    for (const shop of Shops.all()) {
      const open = Shops.isOpen(this.db, shop.id);
      const need = Shops.missingCerts(this.db, shop.id);
      const gate = need.length
        ? `　进门要看${need.map((c) => (CERT_DEFS[c] || {}).name || c).join('、')}`
        : '';
      this.row(body, {
        name: `${shop.name}　${shop.owner}`,
        sub: `${shop.says || ''}　${Shops.openHint(shop.id)}${open ? '' : '　已闭门'}${gate}`,
        acts: [{ act: 'goto', id: shop.id, text: '去看看' }],
        off: !open,
      });
    }
  }

  renderJobs(body) {
    const scenes = [...new Set(JOB_DEFS.map((j) => j.shop))];
    const offers = [];
    for (const s of scenes) offers.push(...Jobs.refreshJobs(this.db, s));
    if (!offers.length) { this.fill(body, '今天没人招工。'); return; }
    for (const off of offers) {
      const left = 3 - (off.claimCount || 0);
      const need = off.needObj && off.needObj.id ? off.needObj.id : '';
      const needCount = off.needObj ? off.needObj.count || 0 : 0;
      const have = need ? Repo.countItem(this.db, need) : 0;
      const shop = Shops.SHOPS[off.giver];
      const needText = need ? `　要${getItem(need).name}${needCount}个（身上${have}）` : '　只出力气';
      this.row(body, {
        name: off.name,
        sub: `${shop ? shop.name : off.giver}　工钱${off.reward_gold}　费${off.ap_cost}点力气${needText}　还能做${left}回`,
        acts: left > 0 ? [{ act: 'claim', id: String(off.id), text: '接下' }] : [],
        off: left <= 0,
      });
    }
  }

  renderQuests(body) {
    const scenes = [...new Set(QUEST_DEFS.map((q) => q.scene))];
    const offers = [];
    for (const s of scenes) offers.push(...Jobs.refreshQuests(this.db, s));
    for (const off of offers) {
      const t = off.target || {};
      const gate = off.gate_cert ? `　要${(CERT_DEFS[off.gate_cert] || {}).name || off.gate_cert}` : '';
      const needText = t.id && t.amount ? `　交${getItem(t.id).name}${t.amount}个` : '　跑一趟';
      this.row(body, {
        name: off.name,
        sub: `${off.has_deadline_days}天内　酬${off.reward_gold}钱${needText}${gate}`,
        acts: [{ act: 'accept', id: String(off.id), text: '揭榜' }],
      });
    }
    const mine = Jobs.activeQuests(this.db);
    for (const q of mine) {
      const t = q.target || {};
      const have = t.id ? Repo.countItem(this.db, t.id) : 0;
      const progress = (q.progress || 0) >= q.required
        ? '够数了'
        : (t.id ? `手上${have}/${q.required}` : `进度${q.progress || 0}/${q.required}`);
      this.row(body, {
        name: `手上：${q.name}`,
        sub: `第${q.deadline}日前　酬${q.reward_gold}钱　${progress}`,
        acts: [{ act: 'redeem', id: String(q.id), text: '交差' }],
      });
    }
    if (!offers.length && !mine.length) this.fill(body, '榜上空着，过几日再看。');
  }

  onBoard(act, id, arg) {
    if (act === 'goto') { this.openShop(id); return; }
    if (act === 'claim') { this.say(Jobs.claimJob(this.db, Number(id))); return; }
    if (act === 'accept') { this.say(Jobs.acceptQuest(this.db, Number(id))); return; }
    if (act === 'redeem') { this.say(Jobs.redeemQuest(this.db, Number(id))); return; }
  }

  /* ------------------------------ 衙署 ------------------------------ */

  renderLicense() {
    const pending = Jobs.pendingLicenses(this.db);
    this.el.licenseInfo.textContent =
      `第${dayOf(this.db)}日　随身${cashOf(this.db)}钱　在办${pending.length}件`;
    const body = this.el.licenseBody;
    body.innerHTML = '';
    for (const [certId, def] of Object.entries(CERT_DEFS)) {
      const row = pending.find((r) => r.needId === certId);
      const owned = Tags.has(this.db, certId);
      let state = '手上有';
      if (!owned && row) state = `办到第${row.stage}步（${(LICENSE_STEPS[row.stage - 1] || {}).name || '等'}）`;
      else if (!owned) state = '还没办';
      const opens = Shops.all().filter((s) => s.needs && s.needs.cert === certId).map((s) => s.name);
      const needers = QUEST_DEFS.filter((q) => q.gate === certId).map((q) => q.name);
      const use = [...opens, ...needers].slice(0, 4).join('、');
      this.row(body, {
        name: def.name,
        sub: `${state}${use ? `　凭它能：${use}` : ''}`,
        acts: owned || row ? [] : [{ act: 'apply', id: certId, text: '递状' }],
        off: owned,
      });
    }
  }

  /* ------------------------------ 地皮 ------------------------------ */

  renderStall() {
    const scenes = [...new Set(UNIT_DEFS.map((u) => u.scene))];
    const units = [];
    for (const s of scenes) units.push(...Jobs.unitsAt(this.db, s));
    const mine = Jobs.myUnits(this.db);
    this.el.stallInfo.textContent =
      `${this.scene && units.some((u) => u.scene === this.scene) ? '此地' : '城里'}　随身${cashOf(this.db)}钱　名下${mine.length}处`;
    const body = this.el.stallBody;
    body.innerHTML = '';
    for (const u of units) {
      if (this.scene && u.scene !== this.scene) continue;
      this.row(body, {
        name: `${u.name}　${u.owned ? '（你的）' : ''}`,
        sub: `买断${u.price}钱　月租${u.rentPerMonth}钱${u.owned ? '' : '　这月没交租'}`,
        acts: u.owned ? [] : [{ act: 'buy', id: u.id, text: '买下' }, { act: 'rent', id: u.id, text: '租一月' }],
        off: u.owned,
      });
    }
    if (!body.children.length) this.fill(body, '这一带没有能盘的地。');
  }

  /* ------------------------------ 短工市 ------------------------------ */

  renderHire() {
    const mine = Jobs.myStaff(this.db);
    const wage = mine.reduce((s, x) => s + (x.wage_per_day_gold || 0), 0);
    this.el.hireInfo.textContent = `随身${cashOf(this.db)}钱　跟你的${mine.length}人　日支${wage}钱`;

    const pool = this.el.hirePool;
    pool.innerHTML = '';
    for (const s of Jobs.hirePool(this.db)) {
      this.row(pool, {
        name: `${s.display_name}　${s.gender || ''}`,
        sub: `身价${s.hire_cost_gold}钱　日工钱${s.wage_per_day_gold}`,
        acts: [{ act: 'hire', id: String(s.id), text: '雇下' }],
      });
    }
    if (!pool.children.length) this.fill(pool, '今儿没人蹲在墙根等活。');

    const box = this.el.hireMine;
    box.innerHTML = '';
    const quests = Jobs.activeQuests(this.db);
    for (const s of mine) {
      const doing = s.task_id ? (Jobs.activeQuests(this.db).find((q) => String(q.id) === String(s.task_id)) || null) : null;
      this.row(box, {
        name: `${s.display_name}　日${s.wage_per_day_gold}钱`,
        sub: doing ? `在做「${doing.name}」` : '闲着',
        acts: quests.slice(0, 3).map((q) => ({ act: 'assign', id: String(s.id), arg: String(q.id), text: q.name.slice(0, 4) })),
      });
    }
    if (!box.children.length) this.fill(box, '还没雇人。');
  }
}
